import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ErrorHandlerService } from '@y42/error-handling';
import { ToastService } from '@y42/toast';
import { takePromise } from '@y42/util/take-promise';
import { DataTest, DataTestRunnerState, RunTestResponseHttpModel, RunTestResponseUIModel } from '../state';
import { DataTestRunnerStore } from '../state/data-test-runner.store';
import { DataTestHttpService } from './data-test-http.service';

@UntilDestroy()
@Injectable()
export class DataTestRunnerService {
  constructor(
    private readonly dataTestHttpService: DataTestHttpService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly store: DataTestRunnerStore,
    private readonly toastService: ToastService,
  ) {}

  async runTest(dataTest: DataTest, path: string): Promise<void> {
    this.updateRunTestState({
      loading: true,
      response: null,
      httpErrorOccurred: false,
    });
    try {
      const headers = this.prepareHeaders([path]);
      const response = await takePromise(
        this.dataTestHttpService.runTest(dataTest, headers).pipe(untilDestroyed(this)),
      );
      this.updateRunTestState({ response: this.toUIModel(response) });
    } catch (e: unknown) {
      this.updateRunTestState({ httpErrorOccurred: true });
      this.errorHandlerService.error('[Data-test] runTest http exception', { context: { error: e } });
    } finally {
      this.updateRunTestState({ loading: false });
    }
  }

  async rerunTestSuite(path: string): Promise<void> {
    this.updateRerunTestSuiteState({
      httpErrorOccurred: false,
      loading: true,
    });
    try {
      await takePromise(this.dataTestHttpService.rerunTestSuite(path).pipe(untilDestroyed(this)));
      this.toastService.success('Data Tests rerun is queued');
    } catch (e: unknown) {
      this.updateRerunTestSuiteState({ httpErrorOccurred: true });
      this.errorHandlerService.error('[Data-test] rerunTestSuite http exception', {
        context: { error: e },
        toast: 'There was an error while rerunning tests',
      });
    } finally {
      this.updateRerunTestSuiteState({ loading: false });
    }
  }

  private toUIModel(httpModel: RunTestResponseHttpModel): RunTestResponseUIModel {
    return httpModel.result;
  }

  private updateRunTestState(runTestState: Partial<DataTestRunnerState['runTest']>) {
    this.store.update((state) => ({ ...state, runTest: { ...state.runTest, ...runTestState } }));
  }

  private updateRerunTestSuiteState(rerunSuiteState: Partial<DataTestRunnerState['rerunTestSuite']>) {
    this.store.update((state) => ({ ...state, rerunTestSuite: { ...state.rerunTestSuite, ...rerunSuiteState } }));
  }

  private prepareHeaders(pathString: string[]): HttpHeaders {
    return new HttpHeaders({
      'x-storage-location': pathString.map((path) => encodeURI(path)),
    });
  }
}
